import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PenSquare, Search, ChevronLeft, ChevronRight, Bookmark, BookmarkCheck, TrendingUp, TrendingDown, SlidersHorizontal, X, Pin, Lock } from 'lucide-react';
import { AdminBadge } from '@/components/atoms/AdminBadge';
import { cn } from '@/lib/cn';
import { queryKeys } from '@/api/queryKeys';
import { get, post as apiPost, del } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { BOARD_BY_SLUG } from '@/constants/boards';
import { type Post } from '@/components/organisms/PostCard';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { Button } from '@/components/atoms/Button';
import { Avatar } from '@/components/atoms/Avatar';
import { Skeleton } from '@/components/atoms/Skeleton';
import { InvestmentDisclaimer } from '@/components/atoms/InvestmentDisclaimer';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { timeAgo } from '@/lib/format';
import { AdBanner } from '@/components/ads/AdBanner';
import { SEOHead } from '@/components/atoms/SEOHead';
import { JsonLd } from '@/components/atoms/JsonLd';

interface DiaryFeedItem {
  id: number;
  ticker: string;
  asset_type: string;
  ticker_name: string | null;
  buy_price: string | null;
  sell_price: string | null;
  quantity: string;
  buy_date: string | null;
  sell_date: string | null;
  profit: string | null;
  profit_rate: string | null;
  buy_reason: string | null;
  memo: string | null;
  created_at: string;
  user_id: string;
  nickname: string;
  profile_image_url: string | null;
  level: number;
}

/** 최초 목적(buy_reason) 코드 → 한글 라벨 (DiaryCreatePage 와 동일 매핑) */
const BUY_REASON_LABEL: Record<string, string> = {
  short_term:  '단타',
  swing:       '스윙',
  mid_term:    '중기',
  long_term:   '장투',
  event:       '이벤트',
  theme:       '테마',
  momentum:    '모멘텀',
  value:       '가치',
  technical:   '기술적',
  fundamental: '재무',
  news:        '증시뉴스',
};

/** 시간 — HH:MM (초 단위 제거). 오늘 작성이 아니면 MM.DD HH:MM */
function diaryTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (sameDay) return hhmm;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}.${dd} ${hhmm}`;
}

function diaryName(d: DiaryFeedItem): string {
  if (d.asset_type === 'coin') {
    const kr = COIN_NAMES_KR[d.ticker] ?? d.ticker_name;
    return kr ? `${kr} (${stripKrw(d.ticker)})` : stripKrw(d.ticker);
  }
  return d.ticker_name ?? d.ticker;
}

/** 게시글 앞 태그 — ticker 있을 때만 종목 한글명 표시 (게시판명 태그 사용 금지 정책) */
function postTag(p: Post): string | null {
  if (p.ticker) {
    const kr = COIN_NAMES_KR[p.ticker];
    if (kr) return kr;
    return stripKrw(p.ticker);
  }
  return null;
}

const PAGE_SIZE = 20;

/** 자유게시판 slug — 카테고리 필터 탭 노출 조건 */
/** (reserved) 이전 자유게시판 specific 로직은 hasCategories 기반으로 대체됨 */

interface BoardCategory {
  id: number;
  board_id: number;
  name: string;
  sort_order: number;
}

export function BoardPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  /* 이벤트 게시판(/board/event)은 노출하지 않고 /events 로 통일 (2026-04-23).
     BoardPage 진입 자체를 막고 리디렉션. */
  useEffect(() => {
    if (slug === 'event') navigate('/events', { replace: true });
  }, [slug, navigate]);

  /* 존재하지 않는 게시판 slug — BOARD_BY_SLUG 매핑 실패 시 404 페이지 렌더링 (아래 if 분기) */

  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const setLoginModalOpen = useUIStore((s) => s.setLoginModalOpen);
  const queryClient = useQueryClient();

  /* 정렬/페이지/카테고리/검색어를 URL 쿼리스트링에 반영 — 상세페이지 방문 후
     브라우저 뒤로가기 시 목록 상태(특히 page)가 1로 리셋되던 문제 수정.
     NewsListPage 와 동일 패턴(useSearchParams). */
  const [params, setParams] = useSearchParams();
  const sort = ((params.get('sort') as 'latest' | 'views' | 'liked' | null) ?? 'latest') as
    | 'latest'
    | 'views'
    | 'liked';
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const categoryIdParam = params.get('categoryId');
  const categoryId: number | '' = categoryIdParam ? Number(categoryIdParam) : '';
  const debouncedKw = params.get('q') ?? '';

  function setSort(next: 'latest' | 'views' | 'liked') {
    const p = new URLSearchParams(params);
    if (next === 'latest') p.delete('sort');
    else p.set('sort', next);
    p.set('page', '1');
    setParams(p);
  }
  function setCategoryId(next: number | '') {
    const p = new URLSearchParams(params);
    if (next === '') p.delete('categoryId');
    else p.set('categoryId', String(next));
    p.set('page', '1');
    setParams(p);
  }
  function setPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p);
  }
  function resetFilters() {
    const p = new URLSearchParams(params);
    p.delete('categoryId');
    p.delete('sort');
    p.set('page', '1');
    setParams(p);
  }

  const [keyword, setKeyword] = useState(() => params.get('q') ?? '');
  /* 매매일지 게시판 전용 필터 — 분류(전체/매수/매도) + 정렬(최신/수익률) */
  const [diaryType, setDiaryType] = useState<'all' | 'buy' | 'sell'>('all');
  const [diarySort, setDiarySort] = useState<'latest' | 'profit_desc' | 'profit_asc'>('latest');
  /** 필터 드롭다운 열림 여부 — 버튼 클릭 시 토글, 외부 클릭 시 닫힘 */
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  /** 게시글 리스트에 말머리(카테고리) 배지 노출 여부 — 기본 숨김 */
  const [showCategoryBadge, setShowCategoryBadge] = useState(false);

  useEffect(() => {
    if (!showFilters) return;
    const onDocClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowFilters(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showFilters]);

  const board = slug ? BOARD_BY_SLUG[slug] : null;
  const isDiaryBoard = slug === 'diary';
  /* 공시 분석 게시판 — 리스트에서 종목 태그(ticker) 비표시 + 작성자 컬럼 → 분석 상태 컬럼 */
  const isNoticeBoard = slug === 'notice';

  /* 카테고리는 모든 게시판에서 조회 시도 — 카테고리 없는 게시판은 빈 배열 반환.
     필터/배지 UI 는 카테고리 개수가 1개 이상일 때만 노출. (2026-04-23: 건의/제안 포함) */
  const { data: categoriesData } = useQuery({
    queryKey: ['boards', board?.id ?? -1, 'categories'],
    queryFn: () =>
      get<{ categories: BoardCategory[] }>(`/boards/${board!.id}/categories`),
    enabled: !!board,
    staleTime: 5 * 60_000,
  });
  const categories = categoriesData?.categories ?? [];
  /* 카테고리가 있는 게시판이면 필터/배지 UI 활성.
     기존 isFreeBoard === 'free' 하드코딩을 제거하고 카테고리 유무로 판정 (건의/제안도 포함) */
  const hasCategories = categories.length > 0;

  /* 키워드 입력 디바운스 (300ms) → 2글자 이상이면 서버 검색어(URL q 파라미터)로 반영.
     키워드 변경 시 페이지 1로 리셋. */
  useEffect(() => {
    const t = setTimeout(() => {
      const kw = keyword.trim();
      const next = kw.length >= 2 ? kw : '';
      if (next !== debouncedKw) {
        const p = new URLSearchParams(params);
        if (next) p.set('q', next);
        else p.delete('q');
        p.set('page', '1');
        setParams(p, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.posts.list(board?.id ?? -1, sort), page, categoryId, debouncedKw],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('boardId', String(board!.id));
      qs.set('sort', sort);
      qs.set('limit', String(PAGE_SIZE));
      qs.set('page', String(page));
      if (categoryId !== '') qs.set('categoryId', String(categoryId));
      if (debouncedKw) qs.set('q', debouncedKw);
      return get<{ posts: Post[]; total: number }>(`/posts?${qs.toString()}`);
    },
    enabled: !!board && !isDiaryBoard,
  });

  const diaryFeed = useQuery({
    queryKey: ['diaries', 'feed'],
    queryFn: () => get<{ feed: DiaryFeedItem[] }>('/diaries/feed'),
    enabled: isDiaryBoard,
    staleTime: 60_000,
  });

  /* AI 종합 분석 — 매시간 cron 으로 갱신된 종목별 종합 카드.
     작성자/인원수 정보 없음. 매매일지 게시판 진입 시에만 조회. */
  const diaryAiSummaries = useQuery({
    queryKey: ['diaries', 'ai-summaries'],
    queryFn: () =>
      get<{
        summaries: Array<{
          ticker: string;
          asset_type: string;
          display_name: string | null;
          ticker_name: string | null;
          summary_html: string;
          generated_at: string;
        }>;
      }>('/diaries/ai-summaries'),
    enabled: isDiaryBoard,
    staleTime: 5 * 60_000,
  });

  /* 필터를 AI 종합 분석에도 함께 적용.
     매수/매도 분류 기준 — AI 분석 종목의 일지 피드 상태로 판단:
       - 'buy'  : 해당 ticker 에 매수 일지(매도 없음) 가 1건 이상 있는 종목
       - 'sell' : 해당 ticker 에 매도 일지(매도 있음) 가 1건 이상 있는 종목
       - 'all'  : 전체 (필터 안 함)
     정렬은 AI 분석에 의미가 없어 generated_at DESC(서버 기본) 그대로 유지. */
  const filteredSummaries = useMemo(() => {
    const summaries = diaryAiSummaries.data?.summaries ?? [];
    if (diaryType === 'all') return summaries;
    const feed = diaryFeed.data?.feed ?? [];
    const buyTickers  = new Set(feed.filter((f) => !f.sell_date).map((f) => f.ticker));
    const sellTickers = new Set(feed.filter((f) => !!f.sell_date).map((f) => f.ticker));
    const allowed = diaryType === 'buy' ? buyTickers : sellTickers;
    return summaries.filter((s) => allowed.has(s.ticker));
  }, [diaryAiSummaries.data, diaryFeed.data, diaryType]);

  /* 운영/공지(board 12) 고정글 1건 — notice-ops 이외 게시판에서만 인라인 노출.
     별도 배너 카드 대신 리스트 최상단 행으로 prepend.
     일반 행과 동일하게 조회수/추천/비추천/댓글 카운터까지 함께 반환받음. */
  const noticePinned = useQuery({
    queryKey: ['posts', 'pinned-top1'],
    queryFn: () =>
      get<
        Array<{
          id: number;
          title: string;
          created_at: string;
          category_name: string | null;
          view_count: number;
          like_count: number;
          dislike_count: number;
          comment_count: number;
        }>
      >('/posts/pinned'),
    enabled: !!board && !isDiaryBoard && board.slug !== 'notice-ops',
    staleTime: 5 * 60_000,
  });
  const topNotice = (noticePinned.data ?? [])[0] ?? null;

  /* 관심게시판 등록 여부 (로그인 유저 한정) */
  const favoriteQuery = useQuery({
    queryKey: ['boards', board?.id ?? -1, 'favorite'],
    queryFn: () =>
      get<{ favorited: boolean }>(`/boards/${board!.id}/favorite`),
    enabled: !!board && isLoggedIn,
    staleTime: 60_000,
  });
  const isFavorited = !!favoriteQuery.data?.favorited;
  const favoriteMutation = useMutation({
    mutationFn: async () => {
      if (!board) return { favorited: false };
      if (isFavorited) {
        return del<{ favorited: boolean }>(`/boards/${board.id}/favorite`);
      }
      return apiPost<{ favorited: boolean }>(`/boards/${board.id}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards', board?.id ?? -1, 'favorite'] });
      queryClient.invalidateQueries({ queryKey: ['boards', 'favorites', 'me'] });
    },
  });

  /* 검색은 서버 사이드(q 파라미터)에서 처리 — 종목명/종목코드 매칭 글이
     클라이언트 필터(제목/작성자)에서 다시 제외되지 않도록 서버 결과를 그대로 사용. */
  const filteredPosts = data?.posts ?? [];

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!board) {
    return <NotFoundPage />;
  }

  // BreadcrumbList JSON-LD — 홈 > 게시판
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '홈',
        item: 'https://community.insidemation.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: board.name,
        item: `https://community.insidemation.com/board/${board.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={board.name}
        description={`${board.name}의 최신 게시글을 확인하세요. ${board.description ?? ''}`.trim()}
        canonical={`/board/${board.slug}`}
      />
      <JsonLd data={breadcrumbSchema} />
      <BoardTabs activeSlug={board.slug} />
      <div className="container-base pb-4">
        {/* 공지 배너 — 운영/공지(board 12) 고정글 최대 5개, 전체 게시판 상단 노출 */}
        {/* [중복 제거 2026-04-29] PinnedNoticesBanner 비노출 — 포스트 리스트 자체에서
            is_pinned=TRUE 글이 최상단에 정렬되므로 동일 글이 두 번 표시되는 문제 해결.
            관리자 핀 순서 조정 UI 는 어드민 → 공지사항 관리 페이지에서 가능. */}
        {/* <PinnedNoticesBanner currentSlug={board.slug} /> */}

        {/* 게시판 전체를 흰 배경 카드로 감싸기 */}
        <div className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
        {/* 상단: 제목/설명 + 관심게시판 버튼 */}
        <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold">{board.name}</h1>
            <p className="mt-1 text-sm text-[var(--color-text-sub)]">
              {board.description}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (!isLoggedIn) return setLoginModalOpen(true);
              favoriteMutation.mutate();
            }}
            disabled={favoriteMutation.isPending || !board}
            className={`flex cursor-pointer items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-150 ease-in-out disabled:cursor-not-allowed disabled:opacity-60 ${
              isFavorited
                ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10'
                : 'border-[var(--color-border)] bg-white text-[var(--color-text-sub)] hover:border-gray-400 hover:bg-gray-50 hover:text-[var(--color-text)]'
            }`}
          >
            {isFavorited ? (
              <BookmarkCheck className="h-4 w-4" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
            {isFavorited ? '관심게시판 해제' : '관심게시판 등록'}
          </button>
        </header>

        {isDiaryBoard ? (
          /* 매매일지 게시판 — AI 종합 분석(리스트) + 공개 일지(리스트) 2분할.
             필터는 페이지 상단 한 곳에서 양쪽 섹션 동시 제어. */
          <>
            {/* 공통 필터 — 분류 칩(전체/매수/매도) + 정렬 드롭다운.
               AI 분석과 공개 매매일지 양쪽에 동시 적용. */}
            <div className="mb-5 flex flex-wrap items-center gap-2 px-1">
              <div className="flex gap-1">
                {(
                  [
                    { key: 'all',  label: '전체' },
                    { key: 'buy',  label: '매수' },
                    { key: 'sell', label: '매도' },
                  ] as const
                ).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setDiaryType(t.key)}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                      diaryType === t.key
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] bg-white text-[var(--color-text-sub)] hover:border-[var(--color-primary)]/40',
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <select
                value={diarySort}
                onChange={(e) => setDiarySort(e.target.value as typeof diarySort)}
                className="ml-auto rounded-full border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-text-sub)] hover:border-[var(--color-primary)]/40"
                aria-label="공개 매매일지 정렬"
              >
                <option value="latest">최신순</option>
                <option value="profit_desc">수익률 높은순</option>
                <option value="profit_asc">수익률 낮은순</option>
              </select>
            </div>

            {/* ── 상단: 매매일지 분석 섹션 ──────────────────────── */}
            <section className="mb-2">
              <header className="mb-4 flex flex-wrap items-center justify-between gap-2 px-1">
                <h2 className="text-base font-extrabold text-[var(--color-primary)] sm:text-lg">
                  매매일지 분석
                </h2>
                <span className="text-[11px] text-[var(--color-text-sub)]">
                  매 1시간 자동 업데이트
                </span>
              </header>
              {diaryAiSummaries.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : filteredSummaries.length > 0 ? (
                /* 공개 매매일지와 동일한 리스트 형태 — border-b 로 구분되는 row 구조 */
                <>
                  {/* 컬럼 헤더 — PC 에서만 노출 */}
                  <div className="hidden items-center gap-3 border-b border-[var(--color-border)] py-2 text-sm text-gray-400 sm:flex">
                    <span className="block min-w-[140px] max-w-[140px]">종목</span>
                    <span className="min-w-0 flex-1">종합 분석</span>
                  </div>
                  <ul>
                    {filteredSummaries.map((s) => (
                      <li
                        key={s.ticker}
                        className="border-b border-[var(--color-border)] last:border-b-0"
                      >
                        <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-3">
                          {/* 좌: 종목명 + ticker — 모바일은 상단 한 줄, PC 는 좌측 컬럼 */}
                          <div className="shrink-0 sm:min-w-[140px] sm:max-w-[140px]">
                            <span className="text-sm font-semibold text-[var(--color-text)]">
                              {s.ticker_name ?? s.display_name ?? s.ticker}
                            </span>
                            <span className="ml-1.5 text-xs text-[var(--color-text-sub)]">
                              {s.ticker}
                            </span>
                          </div>
                          {/* 우: 종합 분석 본문 + 목적 — purpose 폰트 sm + primary 색상 + 굵기 */}
                          <div
                            className="min-w-0 flex-1 text-sm leading-relaxed text-[var(--color-text)] [&_.purpose]:mt-1.5 [&_.purpose]:text-sm [&_.purpose]:font-medium [&_.purpose]:text-[var(--color-primary)] [&_p]:mb-0"
                            dangerouslySetInnerHTML={{ __html: s.summary_html }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-sm font-semibold text-[var(--color-text)]">
                    {diaryType === 'all'
                      ? '아직 분석된 종목이 없어요'
                      : '조건에 맞는 분석이 없어요'}
                  </p>
                  <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                    {diaryType === 'all'
                      ? '매매일지가 쌓이면 AI 가 종합해 보여줄게요'
                      : '다른 필터로 다시 확인해보세요'}
                  </p>
                </div>
              )}
            </section>

            {/* ── 섹션 구분선 ───────────────────────────────────── */}
            <div className="my-8 h-px bg-gradient-to-r from-transparent via-[var(--color-border)] to-transparent" />

            {/* ── 하단: 공개 매매일지 섹션 — 리스트(테이블) 형태 ───── */}
            <header className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
              <h2 className="text-base font-extrabold sm:text-lg">공개 매매일지</h2>
            </header>
            {diaryFeed.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : (diaryFeed.data?.feed?.length ?? 0) > 0 ? (() => {
              /* 클라이언트 사이드 필터링 + 정렬 — 피드 20건 한정이라 부담 없음 */
              const filtered = (diaryFeed.data?.feed ?? []).filter((d) => {
                if (diaryType === 'buy')  return !d.sell_date;       // 매수만 (보유중/매수 단독)
                if (diaryType === 'sell') return !!d.sell_date;       // 매도 완료된 것만
                return true;
              });
              const sorted = [...filtered].sort((a, b) => {
                if (diarySort === 'profit_desc' || diarySort === 'profit_asc') {
                  const ra = Number(a.profit_rate ?? 0);
                  const rb = Number(b.profit_rate ?? 0);
                  // 매도 안 된 건(rate=0)은 항상 뒤로
                  const aHas = !!a.sell_date;
                  const bHas = !!b.sell_date;
                  if (aHas !== bHas) return aHas ? -1 : 1;
                  return diarySort === 'profit_desc' ? rb - ra : ra - rb;
                }
                // 최신순 — 기본 API 응답 순서가 created_at DESC 이므로 유지
                return 0;
              });
              if (sorted.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-sm font-semibold text-[var(--color-text)]">
                      조건에 맞는 매매일지가 없어요
                    </p>
                    <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                      다른 필터로 다시 확인해보세요
                    </p>
                  </div>
                );
              }
              return (
              <>
                {/* 컬럼 헤더 — PC 에서만 노출 */}
                <div className="hidden items-center gap-3 border-b border-[var(--color-border)] py-2 text-sm text-gray-400 sm:flex">
                  <span className="min-w-0 flex-1">종목 · 매매 이유</span>
                  <span className="block min-w-[100px] max-w-[100px] text-left">작성자</span>
                  <span className="inline-flex min-w-[88px] max-w-[88px] justify-end">수익률</span>
                  <span className="inline-flex min-w-[56px] justify-end">시간</span>
                </div>
                <ul>
                  {sorted.map((d) => {
                    const isSell = !!d.sell_date;
                    const rate = Number(d.profit_rate ?? 0);
                    const reasonLabel = d.buy_reason
                      ? BUY_REASON_LABEL[d.buy_reason] ?? d.buy_reason
                      : null;
                    /* 매매 이유 한 줄 — memo 우선, 없으면 buy_reason 라벨, 둘 다 없으면 dash */
                    const reasonText = (() => {
                      if (d.memo && d.memo.trim()) {
                        return d.memo.replace(/\s+/g, ' ').trim();
                      }
                      if (reasonLabel) return `${reasonLabel} 거래`;
                      return null;
                    })();
                    const rateColor =
                      !isSell
                        ? 'text-[var(--color-primary)]'
                        : rate > 0
                        ? 'text-[var(--color-rise)]'
                        : rate < 0
                        ? 'text-[var(--color-fall)]'
                        : 'text-[var(--color-text-sub)]';
                    return (
                      <li
                        key={d.id}
                        className="border-b border-[var(--color-border)] last:border-b-0"
                      >
                        <Link
                          to={`/diary/public/${encodeURIComponent(d.ticker)}`}
                          className="flex items-center gap-3 py-3 hover:bg-[var(--color-bg)]/60"
                        >
                          {/* 좌: 매수/매도 배지 + 최초목적 태그 + 종목명 + 매매 이유 */}
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            <span className={isSell ? 'badge badge-fall' : 'badge badge-rise'}>
                              {isSell ? '매도' : '매수'}
                            </span>
                            {reasonLabel && (
                              <span className="hidden shrink-0 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-sub)] sm:inline-flex">
                                {reasonLabel}
                              </span>
                            )}
                            <span className="min-w-0 flex-1 truncate">
                              <span className="text-sm font-semibold text-[var(--color-text)]">
                                {diaryName(d)}
                              </span>
                              {reasonText && (
                                <>
                                  <span className="mx-1.5 text-[var(--color-text-sub)]">·</span>
                                  <span className="text-xs text-[var(--color-text-sub)]">
                                    {reasonText}
                                  </span>
                                </>
                              )}
                            </span>
                            {/* 모바일 전용 우측 인라인 — 보유중/수익률 */}
                            <span className="shrink-0 sm:hidden">
                              {isSell ? (
                                <span className={`text-xs font-semibold tabular-nums ${rateColor}`}>
                                  {rate > 0 ? '+' : ''}{rate.toFixed(2)}%
                                </span>
                              ) : (
                                <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[11px] font-semibold text-[var(--color-primary)]">
                                  보유 중
                                </span>
                              )}
                            </span>
                          </span>
                          {/* 우: 닉네임(작게) + 수익률/보유중 + 시간 — PC 전용 */}
                          <div className="hidden shrink-0 items-center gap-3 text-xs text-[var(--color-text-sub)] sm:flex">
                            <span className="block min-w-[100px] max-w-[100px] truncate text-left">
                              {d.nickname}
                            </span>
                            <span className="inline-flex min-w-[88px] max-w-[88px] items-center justify-end gap-0.5 font-semibold tabular-nums">
                              {isSell ? (
                                <span className={`inline-flex items-center gap-0.5 ${rateColor}`}>
                                  {rate > 0 ? (
                                    <TrendingUp className="h-3 w-3" />
                                  ) : rate < 0 ? (
                                    <TrendingDown className="h-3 w-3" />
                                  ) : null}
                                  {rate > 0 ? '+' : ''}{rate.toFixed(2)}%
                                </span>
                              ) : (
                                <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[11px] font-semibold text-[var(--color-primary)]">
                                  보유 중
                                </span>
                              )}
                            </span>
                            <span className="inline-flex min-w-[56px] justify-end tabular-nums">
                              {diaryTime(d.created_at)}
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
              );
            })() : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm font-semibold text-[var(--color-text)]">
                  아직 공개된 매매일지가 없어요
                </p>
                <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                  매매일지를 작성하고 공개하면 포인트를 받을 수 있어요 🎁
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {/*
             * 검색 + 필터 버튼 + 글쓰기 (Total 개수 표시 금지 정책)
             * 모바일: [검색 flex-1 | 필터]  ← 1행 풀폭
             *         [글쓰기 w-full]       ← 2행 풀폭 (flex-wrap)
             * sm+  : [검색(w-72) | 필터 ..... 글쓰기]  ← 1행, justify-between
             */}
            <div className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3 sm:justify-between">
              <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-1">
                <div className="relative min-w-0 flex-1 sm:w-72 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]" />
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="제목·종목명·종목코드 검색 (2글자 이상)"
                    className="h-10 w-full rounded-full border border-[var(--color-border)] bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
                  />
                  {/* 1글자 입력 시 힌트 — 2글자 이상 필요 안내 */}
                  {keyword.trim().length === 1 && (
                    <p className="absolute left-3 top-full mt-1 text-[11px] text-[var(--color-text-sub)]">
                      2글자 이상 입력해 주세요.
                    </p>
                  )}
                </div>

                {/*
                 * 필터 버튼 + 드롭다운 팝오버
                 * — 기존 2개 필터(분류/정렬) 를 컴팩트 드롭다운 안으로 수납
                 * — 기본값(전체/최신순) 이 아닐 때 버튼 우측에 파란 점으로 활성 상태 표시
                 * — relative wrapper + absolute panel, 외부 클릭/Esc 로 닫힘
                 */}
                <div ref={filterRef} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowFilters((v) => !v)}
                    aria-expanded={showFilters}
                    aria-haspopup="true"
                    className={`inline-flex h-10 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors sm:px-4 ${
                      showFilters
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] bg-white text-[var(--color-text)] hover:border-[var(--color-primary)]/40'
                    }`}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    <span className="hidden sm:inline">필터</span>
                    {(categoryId !== '' || sort !== 'latest') && (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]"
                        aria-label="필터 적용중"
                      />
                    )}
                  </button>

                  {showFilters && (
                    <div className="absolute right-0 top-full z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[var(--color-border)] bg-white shadow-lg">
                      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2.5">
                        <span className="text-sm font-bold">필터</span>
                        <button
                          type="button"
                          onClick={() => setShowFilters(false)}
                          className="rounded-full p-1 text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]"
                          aria-label="필터 닫기"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {/* 분류 — 자유게시판 전용 */}
                      {hasCategories && (
                        <div className="border-b border-[var(--color-border)] px-4 py-3">
                          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                            분류
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              onClick={() => setCategoryId('')}
                              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                categoryId === ''
                                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                                  : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
                              }`}
                            >
                              전체
                            </button>
                            {categories.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => setCategoryId(c.id)}
                                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                  categoryId === c.id
                                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                                    : 'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
                                }`}
                              >
                                {c.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 정렬 — segmented full-width */}
                      <div className="px-4 py-3">
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                          정렬
                        </div>
                        <div className="flex overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)]">
                          {([
                            { key: 'latest' as const, label: '최신순', hint: '작성일 기준' },
                            { key: 'views' as const, label: '조회순', hint: '조회수 기준' },
                            { key: 'liked' as const, label: '추천순', hint: '추천수 기준' },
                          ]).map((s, i) => (
                            <button
                              key={s.key}
                              onClick={() => setSort(s.key)}
                              title={s.hint}
                              className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                                i > 0 ? 'border-l border-[var(--color-border)]' : ''
                              } ${
                                sort === s.key
                                  ? 'bg-[var(--color-primary)] text-white'
                                  : 'text-[var(--color-text-sub)] hover:bg-white hover:text-[var(--color-text)]'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 표시 옵션 — 말머리 배지 토글 (자유게시판 전용) */}
                      {hasCategories && (
                        <div className="border-t border-[var(--color-border)] px-4 py-3">
                          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-sub)]">
                            표시 옵션
                          </div>
                          <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={showCategoryBadge}
                              onChange={(e) => setShowCategoryBadge(e.target.checked)}
                              className="h-4 w-4 cursor-pointer rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-primary)]/40"
                            />
                            <span className="text-[var(--color-text)]">말머리 표시</span>
                          </label>
                        </div>
                      )}

                      {/* 초기화 — 기본값이 아닐 때만 노출 */}
                      {(categoryId !== '' || sort !== 'latest') && (
                        <div className="border-t border-[var(--color-border)] px-4 py-2.5 text-right">
                          <button
                            onClick={resetFilters}
                            className="text-xs font-medium text-[var(--color-text-sub)] hover:text-[var(--color-primary)]"
                          >
                            초기화
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 글쓰기 버튼 비노출 게시판:
                   - notice-ops: 어드민의 공지사항 관리에서만 작성
                   - notice (공시): DART 자동 수집 전용 — 사용자 작성 차단 */}
              {board.slug !== 'notice-ops' && board.slug !== 'notice' && (
                <button
                  onClick={() => {
                    /* 비로그인 시 메인으로 튕기지 않고 로그인 모달만 띄움
                       (WritePage 진입 후 빈 화면 처리되던 문제 차단) */
                    if (!isLoggedIn) return setLoginModalOpen(true);
                    navigate(`/write?board=${board.slug}`);
                  }}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-primary-inv)] transition-all duration-150 ease-in-out hover:brightness-110 active:brightness-95 sm:w-auto sm:shrink-0"
                >
                  <PenSquare className="h-4 w-4" />
                  글쓰기
                </button>
              )}
            </div>

            {/* 공시 게시판 안내 — AI 분석 지원 범위 (중요=자동 / 참고=요청 시 / 그 외=미지원) */}
            {isNoticeBoard && (
              <p className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2.5 text-xs leading-relaxed text-[var(--color-text-sub)]">
                '중요' 자동분석, '참고' 요청시 분석 그 외 주가에 영향이 미비한 공시는 지원하지 않습니다.
              </p>
            )}

            {/* 게시글 행 리스트 */}
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
                {keyword.trim().length >= 2
                  ? `"${keyword}" 검색 결과가 없어요.`
                  : '아직 게시글이 없어요. 첫 글을 남겨보세요!'}
              </div>
            ) : (
              <>
                {/* 컬럼 헤더 — PC 에서만 노출, 리스트와 동일 너비.
                    공시 게시판은 '작성자' 대신 '분석' 컬럼 (시세봇 → 분석완료/분석중 뱃지) */}
                <div className="hidden items-center gap-3 border-b border-[var(--color-border)] py-2 text-sm text-gray-400 sm:flex">
                  <span className="min-w-0 flex-1">제목</span>
                  <span className="block min-w-[168px] max-w-[168px] text-left">
                    {isNoticeBoard ? '분석' : '작성자'}
                  </span>
                  <span className="inline-flex min-w-[56px] max-w-[56px] justify-end">조회수</span>
                  <span className="inline-flex min-w-[44px] max-w-[44px] justify-end">추천</span>
                  <span className="inline-flex min-w-[44px] max-w-[44px] justify-end">비추천</span>
                  <span className="inline-flex min-w-[64px] justify-end">작성일</span>
                </div>
                <ul>
                  {/* 운영/공지 핀 1건 — 다른 게시판 리스트 최상단에 인라인 노출 (배너 X). */}
                  {topNotice && page === 1 && (() => {
                    const nViews = Math.min(99999, topNotice.view_count ?? 0);
                    const nLikes = Math.min(9999, topNotice.like_count ?? 0);
                    const nDislikes = Math.min(9999, topNotice.dislike_count ?? 0);
                    const nComments = topNotice.comment_count ?? 0;
                    return (
                      <li
                        key={`notice-${topNotice.id}`}
                        className="border-b border-[var(--color-border)]"
                      >
                        <Link
                          to={`/post/${topNotice.id}`}
                          className="flex items-center gap-3 py-3 hover:bg-[var(--color-bg)]/60"
                        >
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            <span className="badge badge-primary">
                              <Pin className="mr-0.5 h-3 w-3" /> 공지
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-medium">
                              {topNotice.title}
                              {nComments > 0 && (
                                <span className="ml-1 font-semibold text-[var(--color-primary)]">
                                  [{nComments}]
                                </span>
                              )}
                            </span>
                          </span>
                          <div className="hidden shrink-0 items-center gap-3 text-xs text-[var(--color-text-sub)] sm:flex">
                            <div className="flex min-w-[168px] max-w-[168px] items-center gap-1.5">
                              {/* 운영자 — 일반 행과 동일한 Avatar(기본 폴백 아이콘) 적용 */}
                              <Avatar alt="운영자" size="xs" />
                              <span className="block min-w-0 flex-1 truncate font-medium text-[var(--color-text)]">
                                운영자
                              </span>
                            </div>
                            <span className="inline-flex min-w-[56px] max-w-[56px] justify-end tabular-nums">
                              {nViews.toLocaleString('ko-KR')}
                            </span>
                            <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                              {nLikes.toLocaleString('ko-KR')}
                            </span>
                            <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                              {nDislikes.toLocaleString('ko-KR')}
                            </span>
                            <span className="inline-flex min-w-[64px] justify-end tabular-nums">
                              {timeAgo(topNotice.created_at)}
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })()}
                  {filteredPosts.map((p) => {
                    const tag = postTag(p);
                    const views = Math.min(99999, p.view_count ?? 0);
                    const likes = Math.min(9999, p.like_count ?? 0);
                    const dislikes = Math.min(9999, p.dislike_count ?? 0);
                    return (
                      <li
                        key={p.id}
                        className={cn(
                          'border-b border-[var(--color-border)] last:border-b-0',
                          p.is_pinned && 'bg-[color-mix(in_srgb,var(--color-primary)_4%,transparent)]',
                        )}
                      >
                        <Link
                          to={`/post/${p.id}`}
                          className="flex items-center gap-3 py-3 hover:bg-[var(--color-bg)]/60"
                        >
                          <span className="flex min-w-0 flex-1 items-center gap-2">
                            {/* 고정글 표시 — 배지 + pin 아이콘 */}
                            {p.is_pinned && (
                              <span className="badge badge-primary">
                                <Pin className="mr-0.5 h-3 w-3" /> 고정
                              </span>
                            )}
                            {/* 비공개 글 표시 — 본인/admin 만 보이는 글 */}
                            {p.is_private && (
                              <span className="badge badge-muted" title="본인과 운영자만 볼 수 있어요">
                                <Lock className="mr-0.5 h-3 w-3" /> 숨김
                              </span>
                            )}
                            {/* 자유게시판 말머리(카테고리) 배지 — 필터 옵션 ON 일 때만 노출 */}
                            {/* 태그 h-5 leading-none 고정 — 토글/유무에 따른 행 높이 흔들림 방지 */}
                            {hasCategories && showCategoryBadge && p.category_name && (
                              <span className="badge badge-muted">{p.category_name}</span>
                            )}
                            {/* 공시 게시판은 종목 태그 비표시 — 제목에 종목명이 이미 포함 */}
                            {tag && !isNoticeBoard && (
                              <span className="badge badge-primary">{tag}</span>
                            )}
                            {/* 공시 중요도 배지 — CRITICAL=[중요] / IMPORTANT=[참고] / NORMAL=[일반].
                                SKIP 은 is_displayed=FALSE 로 목록에서 이미 제외됨 */}
                            {isNoticeBoard && p.importance && (
                              <span
                                className={cn(
                                  'badge',
                                  p.importance === 'CRITICAL' && 'badge-rise',
                                  p.importance === 'IMPORTANT' && 'badge-primary',
                                  p.importance === 'NORMAL' && 'badge-muted',
                                )}
                              >
                                {p.importance === 'CRITICAL'
                                  ? '중요'
                                  : p.importance === 'IMPORTANT'
                                    ? '참고'
                                    : '일반'}
                              </span>
                            )}
                            <span className="min-w-0 flex-1 truncate text-sm font-medium">
                              {p.title}
                              {p.comment_count > 0 && (
                                <span className="ml-1 font-semibold text-[var(--color-primary)]">
                                  [{p.comment_count}]
                                </span>
                              )}
                            </span>
                          </span>
                          {/* 작성자 + 통계 — 헤더와 동일 너비 (라벨 제거, 숫자만).
                              공시 게시판은 작성자(시세봇) 대신 분석 상태 뱃지 표시. */}
                          <div className="hidden shrink-0 items-center gap-3 text-xs text-[var(--color-text-sub)] sm:flex">
                            <div className="flex min-w-[168px] max-w-[168px] items-center gap-1.5">
                              {isNoticeBoard ? (
                                p.analyzed ? (
                                  <span className="inline-flex items-center rounded-full bg-[var(--color-primary)]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[var(--color-primary)]">
                                    분석완료
                                  </span>
                                ) : p.importance === 'IMPORTANT' ? (
                                  /* '참고' 는 자동 분석 대상이 아님 — 글 상세에서 요청해야 분석된다 */
                                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-500">
                                    요청 시 분석
                                  </span>
                                ) : p.importance === 'NORMAL' ? (
                                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-400">
                                    미지원
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-500">
                                    분석중
                                  </span>
                                )
                              ) : (
                                <>
                                  <Avatar
                                    src={p.author_image ?? undefined}
                                    alt={p.author_nickname}
                                    size="xs"
                                    userId={p.user_id}
                                  />
                                  <span className="block min-w-0 flex-1 truncate font-medium text-[var(--color-text)]">
                                    {p.author_nickname}
                                  </span>
                                  <AdminBadge role={p.author_admin_role} />
                                </>
                              )}
                            </div>
                            <span className="inline-flex min-w-[56px] max-w-[56px] justify-end tabular-nums">
                              {views.toLocaleString('ko-KR')}
                            </span>
                            <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                              {likes.toLocaleString('ko-KR')}
                            </span>
                            <span className="inline-flex min-w-[44px] max-w-[44px] justify-end tabular-nums">
                              {dislikes.toLocaleString('ko-KR')}
                            </span>
                            <span className="inline-flex min-w-[64px] justify-end tabular-nums">
                              {timeAgo(p.created_at)}
                            </span>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={(n) => setPage(n)}
              />
            )}
          </>
        )}
        </div>

        {/* isDiaryBoard 전용 하단 안내 — 카드 외부 */}
        {isDiaryBoard && (
          <div className="mt-4 flex justify-end">
            <Button onClick={() => navigate('/diary')}>
              <PenSquare className="h-4 w-4" />
              내 매매일지
            </Button>
          </div>
        )}

        {/* 페이지 하단 광고 — 모바일에서 multiplex-h 가 세로로 길게 늘어나 정형 디스플레이로 교체 (2026-04-30) */}
        <AdBanner slot="display-rect" className="mt-3 sm:mt-6" />

        {/* 페이지 하단 공통 면책조항 (compact) */}
        <InvestmentDisclaimer variant="compact" className="mt-6" />
      </div>
    </>
  );
}


/** 숫자형 페이지네이션 — 현재 페이지 중심으로 최대 5개 버튼 */
function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (n: number) => void;
}) {
  const windowSize = 5;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, Math.min(start, end - windowSize + 1));
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="mt-6 flex items-center justify-center gap-1">
      <button
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-sub)] hover:bg-[var(--color-bg)] disabled:opacity-40"
        aria-label="이전 페이지"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      {pages.map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`h-10 min-w-[40px] rounded-lg px-2 text-sm font-semibold transition-colors ${
            n === page
              ? 'bg-[var(--color-primary)] text-white'
              : 'border border-[var(--color-border)] text-[var(--color-text-sub)] hover:bg-[var(--color-bg)]'
          }`}
        >
          {n}
        </button>
      ))}
      <button
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-sub)] hover:bg-[var(--color-bg)] disabled:opacity-40"
        aria-label="다음 페이지"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
